import { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'clsx';
import { connect } from 'react-redux';
import interpolateComponents from 'interpolate-components';
import { bindActionCreators } from 'redux';
import get from 'lodash/get';

import UnderlineLink from '../shared/link/UnderlineLink';
import ChevronRightLink from '../shared/link/ChevronRightLink';

import { openOneTrustGdprSettingsDialog } from '../../actions/app';

import getCurrentYear from '../../utils/date/getCurrentYear';
import {
  shouldShowCcpaOptOut,
  shouldShowCcpaPolicyPageLink,
} from '../utils/ccpa';

import siteLinks from '../../constants/footer/siteLinks';
import * as localizations from '../../constants/localizations/site';
import socialLinks from '../../constants/footer/socialLinks';

import { LocationAndLocalizationContext } from '../../providers/LocationAndLocalizationProvider';
import css from './footer.module.scss';

// exported for testing
export class Footer extends Component {
  static propTypes = {
    isRightSideOpen: PropTypes.bool,
    isLanding: PropTypes.bool,
    shouldShowCcpaPolicyPage: PropTypes.bool,
    showCcpaOptOut: PropTypes.bool.isRequired,
    shouldShowOneTrustGdprOptOut: PropTypes.bool.isRequired,
    actions: PropTypes.shape({
      openOneTrustGdprSettingsDialog: PropTypes.func.isRequired,
    }),
  };

  static contextType = LocationAndLocalizationContext;

  render() {
    const {
      isRightSideOpen,
      isLanding,
      shouldShowCcpaPolicyPage,
      showCcpaOptOut,
      shouldShowOneTrustGdprOptOut,
      actions,
    } = this.props;
    const { getLocalizedText } = this.context;
    return (
      <footer
        id="footer"
        data-testid="footer"
        className={classNames({ [css.isLanding]: isLanding })}
      >
        <div
          className={classNames(css.footer, {
            [css.noRight]: !isRightSideOpen,
          })}
        >
          <div className={css.footerRow}>
            <div
              id="footerLogoContainer"
              data-testid="footerLogoContainer"
              className={css.logoContainer}
            >
              <div
                id="footerLogo"
                data-testid="footerLogo"
                className={css.logo}
              />
              <div
                id="footerValueProp"
                data-testid="footerValueProp"
                className={classNames(css.valuePropContainer, {
                  [css.noRight]: !isRightSideOpen,
                })}
              >
                {getLocalizedText(localizations.VALUE_PROP)}
              </div>
            </div>
            <div
              id="siteLinksContainer"
              data-testid="siteLinksContainer"
              className={css.linksContainer}
            >
              <div className={css.linksRow}>
                <div
                  data-testid="listenLinks"
                  className={classNames(css.linksColumn, {
                    [css.noRight]: !isRightSideOpen,
                  })}
                >
                  <ChevronRightLink
                    id={siteLinks.listenNow.id}
                    data-testid={siteLinks.listenNow.id}
                    className={css.siteLinkHeader}
                    chevronClassName={css.chevron}
                    to={siteLinks.listenNow.url}
                    injectSvg={isLanding}
                  >
                    {getLocalizedText(siteLinks.listenNow.name)}
                  </ChevronRightLink>
                  <UnderlineLink
                    id={siteLinks.sports.id}
                    data-testid={siteLinks.sports.id}
                    to={siteLinks.sports.url}
                    className={css.siteLink}
                    textEllipsis
                  >
                    {getLocalizedText(siteLinks.sports.name)}
                  </UnderlineLink>
                  <UnderlineLink
                    id={siteLinks.music.id}
                    data-testid={siteLinks.music.id}
                    to={siteLinks.music.url}
                    className={css.siteLink}
                    textEllipsis
                  >
                    {getLocalizedText(siteLinks.music.name)}
                  </UnderlineLink>
                  <UnderlineLink
                    id={siteLinks.news.id}
                    data-testid={siteLinks.news.id}
                    to={siteLinks.news.url}
                    className={css.siteLink}
                    textEllipsis
                  >
                    {getLocalizedText(siteLinks.news.name)}
                  </UnderlineLink>
                  <UnderlineLink
                    id={siteLinks.podcasts.id}
                    data-testid={siteLinks.podcasts.id}
                    to={siteLinks.podcasts.url}
                    className={css.siteLink}
                    textEllipsis
                  >
                    {getLocalizedText(siteLinks.podcasts.name)}
                  </UnderlineLink>
                  <UnderlineLink
                    id={siteLinks.more.id}
                    data-testid={siteLinks.more.id}
                    to={siteLinks.more.url}
                    className={css.siteLink}
                    textEllipsis
                  >
                    {getLocalizedText(siteLinks.more.name)}
                  </UnderlineLink>
                </div>
                <div
                  data-testid="aboutLinks"
                  className={classNames(css.linksColumn, {
                    [css.noRight]: !isRightSideOpen,
                  })}
                >
                  <ChevronRightLink
                    id={siteLinks.aboutUs.id}
                    data-testid={siteLinks.aboutUs.id}
                    className={css.siteLinkHeader}
                    chevronClassName={css.chevron}
                    to={siteLinks.aboutUs.url}
                    target="_blank"
                    isNotReactRouterLink
                    injectSvg={isLanding}
                  >
                    {getLocalizedText(siteLinks.aboutUs.name)}
                  </ChevronRightLink>
                  <UnderlineLink
                    id={siteLinks.contactUs.id}
                    data-testid={siteLinks.contactUs.id}
                    className={css.siteLink}
                    to={siteLinks.contactUs.url}
                    target="_blank"
                    isNotReactRouterLink
                    textEllipsis
                  >
                    {getLocalizedText(siteLinks.contactUs.name)}
                  </UnderlineLink>
                  <UnderlineLink
                    id={siteLinks.careers.id}
                    data-testid={siteLinks.careers.id}
                    className={css.siteLink}
                    to={siteLinks.careers.url}
                    target="_blank"
                    isNotReactRouterLink
                    textEllipsis
                  >
                    {getLocalizedText(siteLinks.careers.name)}
                  </UnderlineLink>
                  <UnderlineLink
                    id={siteLinks.press.id}
                    data-testid={siteLinks.press.id}
                    className={css.siteLink}
                    to={siteLinks.press.url}
                    target="_blank"
                    isNotReactRouterLink
                    textEllipsis
                  >
                    {getLocalizedText(siteLinks.press.name)}
                  </UnderlineLink>
                </div>
              </div>
              <div className={css.linksRow}>
                <div
                  data-testid="productLinks"
                  className={classNames(css.linksColumn, css.noLinkHeader, {
                    [css.noRight]: !isRightSideOpen,
                  })}
                >
                  <span
                    id="footerProduct"
                    data-testid="footerProduct"
                    className={classNames(css.siteLinkHeader, css.siteLink)}
                  >
                    {getLocalizedText(localizations.PRODUCT)}
                  </span>
                  <UnderlineLink
                    id={siteLinks.support.id}
                    data-testid={siteLinks.support.id}
                    className={css.siteLink}
                    to={siteLinks.support.url}
                    target="_blank"
                    isNotReactRouterLink
                    textEllipsis
                  >
                    {getLocalizedText(siteLinks.support.name)}
                  </UnderlineLink>
                  <UnderlineLink
                    id={siteLinks.getTuneIn.id}
                    data-testid={siteLinks.getTuneIn.id}
                    className={css.siteLink}
                    to={siteLinks.getTuneIn.url}
                    target="_blank"
                    isNotReactRouterLink
                    textEllipsis
                  >
                    {getLocalizedText(siteLinks.getTuneIn.name)}
                  </UnderlineLink>
                </div>
                <div
                  data-testid="communityLinks"
                  className={classNames(css.linksColumn, css.noLinkHeader, {
                    [css.noRight]: !isRightSideOpen,
                  })}
                >
                  <span
                    id="footerCommunities"
                    data-testid="footerCommunities"
                    className={classNames(css.siteLinkHeader, css.siteLink)}
                  >
                    {getLocalizedText(localizations.COMMUNITIES)}
                  </span>
                  <UnderlineLink
                    id={siteLinks.brand.id}
                    data-testid={siteLinks.brand.id}
                    className={css.siteLink}
                    to={siteLinks.brand.url}
                    target="_blank"
                    isNotReactRouterLink
                    textEllipsis
                  >
                    {getLocalizedText(siteLinks.brand.name)}
                  </UnderlineLink>
                  <UnderlineLink
                    id={siteLinks.broadcasters.id}
                    data-testid={siteLinks.broadcasters.id}
                    className={css.siteLink}
                    to={siteLinks.broadcasters.url}
                    target="_blank"
                    isNotReactRouterLink
                    textEllipsis
                  >
                    {getLocalizedText(siteLinks.broadcasters.name)}
                  </UnderlineLink>
                  <UnderlineLink
                    id={siteLinks.podcasters.id}
                    data-testid={siteLinks.podcasters.id}
                    className={css.siteLink}
                    to={siteLinks.podcasters.url}
                    target="_blank"
                    isNotReactRouterLink
                    textEllipsis
                  >
                    {getLocalizedText(siteLinks.podcasters.name)}
                  </UnderlineLink>
                </div>
              </div>
            </div>
          </div>
          <div className={classNames(css.footerRow, css.noOverflow)}>
            <div
              id="footerFootnoteContainer"
              data-testid="footerFootnoteContainer"
              className={css.footnoteContainer}
            >
              <div
                id="legalContainer"
                data-testid="legalContainer"
                className={css.legalContainer}
              >
                {interpolateComponents({
                  mixedString: getLocalizedText(localizations.LEGAL),
                  components: {
                    year: (
                      <span data-testid="legalYear">{getCurrentYear()}</span>
                    ),
                  },
                })}
              </div>
              <div
                id="policiesContainer"
                data-testid="policiesContainer"
                className={css.policiesContainer}
              >
                <UnderlineLink
                  id={siteLinks.privacy.id}
                  data-testid={siteLinks.privacy.id}
                  className={css.siteLink}
                  to={siteLinks.privacy.url}
                  target="_blank"
                  isNotReactRouterLink
                >
                  {getLocalizedText(siteLinks.privacy.name)}
                </UnderlineLink>
                <UnderlineLink
                  id={siteLinks.policies.id}
                  data-testid={siteLinks.policies.id}
                  className={css.siteLink}
                  to={siteLinks.policies.url}
                  target="_blank"
                  isNotReactRouterLink
                >
                  {getLocalizedText(siteLinks.policies.name)}
                </UnderlineLink>
                <UnderlineLink
                  id={siteLinks.sitemap.id}
                  data-testid={siteLinks.sitemap.id}
                  className={css.siteLink}
                  to={siteLinks.sitemap.url}
                  target="_blank"
                  isNotReactRouterLink
                >
                  {getLocalizedText(siteLinks.sitemap.name)}
                </UnderlineLink>
                {!showCcpaOptOut &&
                  !shouldShowCcpaPolicyPage &&
                  !shouldShowOneTrustGdprOptOut && (
                    <UnderlineLink
                      id={siteLinks.globalOptOut.id}
                      data-testid={siteLinks.globalOptOut.id}
                      className={css.siteLink}
                      to={siteLinks.globalOptOut.url}
                    >
                      {getLocalizedText(siteLinks.globalOptOut.name)}
                    </UnderlineLink>
                  )}
                {showCcpaOptOut && !shouldShowOneTrustGdprOptOut && (
                  <UnderlineLink
                    id={siteLinks.ccpaOptOut.id}
                    data-testid={siteLinks.ccpaOptOut.id}
                    className={css.siteLink}
                    to={siteLinks.ccpaOptOut.url}
                  >
                    {getLocalizedText(siteLinks.ccpaOptOut.name)}
                  </UnderlineLink>
                )}
                {shouldShowCcpaPolicyPage &&
                  !showCcpaOptOut &&
                  !shouldShowOneTrustGdprOptOut && (
                    <UnderlineLink
                      id={siteLinks.dontSellMyData.id}
                      data-testid={siteLinks.dontSellMyData.id}
                      className={css.siteLink}
                      to={siteLinks.dontSellMyData.url}
                      target="_blank"
                      isNotReactRouterLink
                    >
                      {getLocalizedText(siteLinks.dontSellMyData.name)}
                    </UnderlineLink>
                  )}
                {shouldShowOneTrustGdprOptOut && (
                  <UnderlineLink
                    id={siteLinks.oneTrustSettings.id}
                    data-testid={siteLinks.oneTrustSettings.id}
                    className={css.siteLink}
                    onClick={actions.openOneTrustGdprSettingsDialog}
                  >
                    {getLocalizedText(siteLinks.oneTrustSettings.name)}
                  </UnderlineLink>
                )}
              </div>
            </div>
            <div
              id="socialLinksContainer"
              data-testid="socialLinksContainer"
              className={css.socialLinksContainer}
            >
              {socialLinks.map(({ name, cssClass, url }) => (
                <a
                  id={`footerSocial${name}`}
                  data-testid={`footerSocial${name}`}
                  key={`footerSocial${name}`}
                  className={classNames(
                    css.socialLink,
                    css.socialIcon,
                    css[cssClass],
                    { [css.noRight]: !isRightSideOpen },
                  )}
                  href={url}
                  target="_blank"
                ></a>
              ))}
            </div>
          </div>
        </div>
      </footer>
    );
  }
}

// exported for testing
export function mapStateToProps({ app, config }) {
  return {
    shouldShowCcpaPolicyPage: shouldShowCcpaPolicyPageLink(config),
    showCcpaOptOut: shouldShowCcpaOptOut(config),
    shouldShowOneTrustGdprOptOut: get(app, 'oneTrust.gdpr.gdprApplies', false),
  };
}

// exported for testing
export function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators({ openOneTrustGdprSettingsDialog }, dispatch),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(Footer);
